
import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import Loader from "../components/Loader";
import Pagination from "../components/Pagination";
import CreateShopModal from "../components/CreateShopModal";

import shopService from "../services/shopService";

const ITEMS_PER_PAGE = 8;

const Shops = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [showCreateModal, setShowCreateModal] =
    useState(false);

  const [selectedShop, setSelectedShop] =
    useState(null);

  const [statusUpdatingId, setStatusUpdatingId] =
    useState(null);

  const [shopToDelete, setShopToDelete] =
    useState(null);
  const [deleting, setDeleting] =
    useState(false);

  // =====================================================
  // SIDEBAR
  // =====================================================

  const toggleSidebar = () => {
    setSidebarOpen((previous) => !previous);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // =====================================================
  // FORMAT COINS
  // =====================================================

  const formatCoins = (amount) => {
    return Number(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // =====================================================
  // FETCH SHOPS
  // =====================================================

  const fetchShops = async ({
    silent = false,
  } = {}) => {
    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const response = await shopService.getShops();

      if (!response?.success) {
        setShops([]);

        toast.error(
          response?.message ||
            "Unable to load Shops."
        );

        return;
      }

      setShops(
        Array.isArray(response.shops)
          ? response.shops
          : []
      );
    } catch (error) {
      console.error(
        "Load Shops Error:",
        error
      );

      toast.error(
        error.response?.data?.message ||
          "Unable to load Shops."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchShops();
  }, []);

  // =====================================================
  // SEARCH RESET PAGE
  // =====================================================

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // =====================================================
  // FILTER SHOPS
  // =====================================================

  const filteredShops = useMemo(() => {
    const query = searchQuery
      .trim()
      .toLowerCase();

    if (!query) {
      return shops;
    }

    return shops.filter((shop) => {
      return (
        String(shop.shop_name || "")
          .toLowerCase()
          .includes(query) ||
        String(shop.owner_full_name || "")
          .toLowerCase()
          .includes(query) ||
        String(shop.owner_username || "")
          .toLowerCase()
          .includes(query) ||
        String(shop.mobile || "")
          .toLowerCase()
          .includes(query) ||
        String(shop.address || "")
          .toLowerCase()
          .includes(query) ||
        String(shop.status || "")
          .toLowerCase()
          .includes(query)
      );
    });
  }, [shops, searchQuery]);

  // =====================================================
  // PAGINATION
  // =====================================================

  const totalPages = Math.max(
    1,
    Math.ceil(
      filteredShops.length /
        ITEMS_PER_PAGE
    )
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const currentShops = useMemo(() => {
    const startIndex =
      (currentPage - 1) *
      ITEMS_PER_PAGE;

    return filteredShops.slice(
      startIndex,
      startIndex +
        ITEMS_PER_PAGE
    );
  }, [
    filteredShops,
    currentPage,
  ]);

  // =====================================================
  // SHOP STATUS
  // =====================================================

  const isShopActive = (shop) => {
    const shopStatus = String(
      shop?.status || "ACTIVE"
    ).toUpperCase();

    const ownerStatus = String(
      shop?.owner_status || "ACTIVE"
    ).toUpperCase();

    return (
      (shopStatus === "ACTIVE") &&
      (ownerStatus === "ACTIVE") &&
      shopStatus !== "INACTIVE" &&
      shopStatus !== "BLOCKED" &&
      ownerStatus !== "INACTIVE" &&
      ownerStatus !== "BLOCKED"
    );
  };

  const activeShops = shops.filter(
    isShopActive
  ).length;

  const blockedShops =
    shops.length - activeShops;

  // =====================================================
  // WALLET TOTAL
  // =====================================================

  const totalShopWallet = shops.reduce(
    (total, shop) =>
      total +
      Number(shop.wallet || 0),
    0
  );

  // =====================================================
  // BLOCK / UNBLOCK SHOP
  // =====================================================

  const handleToggleStatus = async (shop) => {
    const nextStatus =
      isShopActive(shop)
        ? "INACTIVE"
        : "ACTIVE";

    try {
      setStatusUpdatingId(shop.id);

      const ownerId = shop.owner_id || shop.shop_owner_id || shop.user_id || shop.id;

      const response =
        await shopService.updateShopStatus(
          shop.id,
          nextStatus,
          ownerId
        );

      if (!response?.success) {
        toast.error(
          response?.message ||
            "Unable to update Shop status."
        );

        return false;
      }

      toast.success(
        response.message ||
          `Shop ${nextStatus === "INACTIVE" ? "blocked" : "activated"} successfully.`
      );

      await fetchShops({
        silent: true,
      });

      return true;
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Unable to update Shop status."
      );

      return false;
    } finally {
      setStatusUpdatingId(null);
    }
  };

  // =====================================================
  // DELETE SHOP
  // =====================================================

  const handleDeleteShop = async (shop) => {
    if (!shop) return;

    try {
      setDeleting(true);
      const response = await shopService.deleteShop(shop.id);

      if (!response?.success) {
        toast.error(response?.message || "Failed to delete Shop.");
        return;
      }

      toast.success(response.message || "Shop deleted successfully.");
      setShopToDelete(null);
      if (selectedShop?.id === shop.id) {
        setSelectedShop(null);
      }
      await fetchShops({ silent: true });
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to delete Shop."
      );
    } finally {
      setDeleting(false);
    }
  };

  // =====================================================
  // RECORD RANGE
  // =====================================================

  const startRecord =
    filteredShops.length === 0
      ? 0
      : (currentPage - 1) *
          ITEMS_PER_PAGE +
        1;

  const endRecord = Math.min(
    currentPage *
      ITEMS_PER_PAGE,
    filteredShops.length
  );

  // =====================================================
  // UI
  // =====================================================

  return (
    <div className="d-flex flex-column min-vh-100 app-shell">
      {/* NAVBAR */}

      <Navbar
        toggleSidebar={toggleSidebar}
      />

      <div className="d-flex flex-grow-1 position-relative">
        {/* SIDEBAR */}

        <Sidebar
          isOpen={sidebarOpen}
          closeMobileSidebar={
            closeSidebar
          }
        />

        {/* MAIN */}

        <main className="app-main flex-grow-1 overflow-auto">
          <div className="container-fluid admin-page-wrap">
            {/* =================================================
                PAGE HEADER
            ================================================= */}

            <div className="admin-page-header">
              <div>
                <div>
                  <span className="admin-page-kicker">
                    DEALER NETWORK
                  </span>

                  <h2>
                    Shops Management
                  </h2>

                  <p>
                    Manage shops registered
                    under your Dealer account.
                  </p>
                </div>
              </div>

              <div className="d-flex flex-wrap gap-2">
                {/* REFRESH */}

                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() =>
                    fetchShops({
                      silent: true,
                    })
                  }
                  disabled={
                    loading ||
                    refreshing
                  }
                >
                  {refreshing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Refreshing...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-arrow-clockwise me-2" />
                      Refresh
                    </>
                  )}
                </button>

                {/* TRANSFER */}

                <Link
                  to="/transfer-shop"
                  className="btn btn-outline-success"
                >
                  <i className="bi bi-send me-2" />
                  Transfer Coins
                </Link>

                {/* CREATE */}

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() =>
                    setShowCreateModal(
                      true
                    )
                  }
                >
                  <i className="bi bi-shop me-2" />
                  Create Shop
                </button>
              </div>
            </div>

            {/* =================================================
                STATS
            ================================================= */}

            <div className="row g-3 mb-4">
              {/* TOTAL SHOPS */}

              <div className="col-12 col-sm-6 col-xl-3">
                <div className="admin-stat-card h-100">
                  <div className="card-body d-flex align-items-center">
                    <div className="bg-primary text-white rounded-3 p-3 me-3">
                      <i className="bi bi-shop fs-3" />
                    </div>

                    <div>
                      <div className="small text-secondary fw-semibold">
                        Total Shops
                      </div>

                      <h3 className="fw-bold mb-0">
                        {shops.length}
                      </h3>
                    </div>
                  </div>
                </div>
              </div>

              {/* ACTIVE */}

              <div className="col-12 col-sm-6 col-xl-3">
                <div className="admin-stat-card h-100">
                  <div className="card-body d-flex align-items-center">
                    <div className="bg-success text-white rounded-3 p-3 me-3">
                      <i className="bi bi-check-circle fs-3" />
                    </div>

                    <div>
                      <div className="small text-secondary fw-semibold">
                        Active Shops
                      </div>

                      <h3 className="fw-bold mb-0">
                        {activeShops}
                      </h3>
                    </div>
                  </div>
                </div>
              </div>

              {/* BLOCKED */}

              <div className="col-12 col-sm-6 col-xl-3">
                <div className="admin-stat-card h-100">
                  <div className="card-body d-flex align-items-center">
                    <div className="bg-danger text-white rounded-3 p-3 me-3">
                      <i className="bi bi-x-circle fs-3" />
                    </div>

                    <div>
                      <div className="small text-secondary fw-semibold">
                        Blocked Shops
                      </div>

                      <h3 className="fw-bold mb-0">
                        {blockedShops}
                      </h3>
                    </div>
                  </div>
                </div>
              </div>

              {/* WALLET */}

              <div className="col-12 col-sm-6 col-xl-3">
                <div className="admin-stat-card h-100">
                  <div className="card-body d-flex align-items-center">
                    <div className="bg-warning text-dark rounded-3 p-3 me-3">
                      <i className="bi bi-wallet2 fs-3" />
                    </div>

                    <div>
                      <div className="small text-secondary fw-semibold">
                        Shop Wallet Total
                      </div>

                      <h5 className="fw-bold mb-0">
                        🪙{" "}
                        {formatCoins(
                          totalShopWallet
                        )}
                      </h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* =================================================
                SEARCH
            ================================================= */}

            <div className="admin-panel mb-4">
              <div className="card-body p-3">
                <div
                  className="input-group"
                  style={{
                    maxWidth:
                      "500px",
                  }}
                >
                  <span className="input-group-text bg-white">
                    <i className="bi bi-search" />
                  </span>

                  <input
                    type="search"
                    className="form-control"
                    placeholder="Search Shop, owner, username, mobile or address..."
                    value={searchQuery}
                    onChange={(
                      event
                    ) =>
                      setSearchQuery(
                        event.target
                          .value
                      )
                    }
                  />

                  {searchQuery && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() =>
                        setSearchQuery(
                          ""
                        )
                      }
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* =================================================
                SHOPS TABLE
            ================================================= */}

            <div className="admin-panel overflow-hidden">
              <div className="admin-panel-head">
                <h5 className="fw-bold mb-1">
                  Shops List
                </h5>

                <p className="small text-secondary mb-0">
                  Click any Shop row
                  to view details and
                  available actions.
                </p>
              </div>

              <div className="card-body p-0">
                {loading ? (
                  <Loader message="Loading Shops..." />
                ) : currentShops.length ===
                  0 ? (
                  <div className="text-center py-5 px-3">
                    <i className="bi bi-shop display-4 text-secondary" />

                    <h5 className="fw-bold mt-3">
                      No Shops Found
                    </h5>

                    <p className="text-secondary mb-0">
                      {searchQuery
                        ? "No Shop matches your search."
                        : "Create your first Shop account."}
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table admin-table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="ps-4">
                            Sr No
                          </th>

                          <th>
                            Shop
                          </th>

                          <th>
                            Owner
                          </th>

                          <th>
                            Mobile
                          </th>

                          <th>
                            Wallet
                          </th>

                          <th className="pe-4">
                            Status
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {currentShops.map(
                          (shop, index) => (
                            <tr
                              key={
                                shop.id
                              }
                              onClick={() =>
                                setSelectedShop(
                                  shop
                                )
                              }
                              onKeyDown={(
                                event
                              ) => {
                                if (
                                  event.key ===
                                    "Enter" ||
                                  event.key ===
                                    " "
                                ) {
                                  event.preventDefault();

                                  setSelectedShop(
                                    shop
                                  );
                                }
                              }}
                              role="button"
                              tabIndex={0}
                              title="Click to view Shop details"
                              style={{
                                cursor:
                                  "pointer",
                              }}
                            >
                              {/* ID */}

                              <td className="ps-4 fw-bold">
                                {startRecord + index}
                              </td>

                              {/* SHOP */}

                              <td>
                                <div className="fw-semibold text-dark">
                                  {shop.shop_name ||
                                    "Unnamed Shop"}
                                </div>

                                <div className="small text-secondary">
                                  {shop.address ||
                                    "Address not provided"}
                                </div>
                              </td>

                              {/* OWNER */}

                              <td>
                                <div className="fw-semibold">
                                  {shop.owner_full_name ||
                                    "N/A"}
                                </div>

                                <div className="small text-primary">
                                  @
                                  {shop.owner_username ||
                                    "N/A"}
                                </div>
                              </td>

                              {/* MOBILE */}

                              <td>
                                {shop.mobile ||
                                  "N/A"}
                              </td>

                              {/* WALLET */}

                              <td className="fw-bold text-success">
                                🪙{" "}
                                {formatCoins(
                                  shop.wallet
                                )}
                              </td>

                              {/* STATUS */}

                              <td className="pe-4">
                                <div className="d-flex align-items-center justify-content-between gap-3">
                                  <span
                                    className={`badge rounded-pill px-3 py-2 ${
                                      isShopActive(
                                        shop
                                      )
                                        ? "bg-success-subtle text-success border border-success-subtle"
                                        : "bg-danger-subtle text-danger border border-danger-subtle"
                                    }`}
                                  >
                                    {isShopActive(
                                      shop
                                    )
                                      ? "ACTIVE"
                                      : "BLOCKED"}
                                  </span>

                                  <i className="bi bi-chevron-right text-secondary small" />
                                </div>
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* PAGINATION */}

              {!loading &&
                filteredShops.length >
                  0 && (
                  <div className="card-footer bg-white border-top p-3">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                      <div className="small text-secondary">
                        Showing{" "}
                        {startRecord}–
                        {endRecord} of{" "}
                        {
                          filteredShops.length
                        }{" "}
                        Shops
                      </div>

                      <Pagination
                        currentPage={
                          currentPage
                        }
                        totalPages={
                          totalPages
                        }
                        onPageChange={
                          setCurrentPage
                        }
                      />
                    </div>
                  </div>
                )}
            </div>
          </div>
        </main>
      </div>

      {/* =====================================================
          CREATE SHOP MODAL
      ===================================================== */}

      <CreateShopModal
        show={showCreateModal}
        onClose={() =>
          setShowCreateModal(
            false
          )
        }
        onShopCreated={() =>
          fetchShops({
            silent: true,
          })
        }
      />

      {selectedShop && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1050 }}
          />

          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            style={{
              zIndex: 1055,
              overflow: "hidden",
            }}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                setSelectedShop(null);
              }
            }}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              style={{
                maxWidth: "480px",
                margin: "10px auto",
              }}
            >
              <div
                className="modal-content border border-[rgba(148,163,184,0.2)] bg-[#121827] shadow-2xl rounded-2xl overflow-hidden"
                style={{
                  maxHeight: "calc(100vh - 20px)",
                }}
              >
                {/* HEADER */}
                <div className="modal-header bg-[#0D111C] border-b border-[rgba(148,163,184,0.14)] py-3 px-4 d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2.5">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30 text-lg font-bold">
                      ♠
                    </div>
                    <div>
                      <h5 className="modal-title fw-bold text-[#F8FAFC] fs-6 mb-0">
                        Shop Details
                      </h5>
                      <div className="text-[10px] text-[#94A3B8]">
                        @{selectedShop.owner_username || "N/A"}
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    aria-label="Close"
                    onClick={() => setSelectedShop(null)}
                  />
                </div>

                {/* BODY - ESSENTIAL DATA ONLY */}
                <div className="modal-body p-4 space-y-4">
                  {/* SHOP NAME & STATUS BANNER */}
                  <div className="p-3.5 rounded-xl bg-[#0D111C] border border-[rgba(148,163,184,0.14)] d-flex align-items-center justify-content-between">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-[#64748B] mb-0.5">
                        Shop Name
                      </div>
                      <div className="fw-bold fs-5 text-[#F8FAFC]">
                        {selectedShop.shop_name || "Unnamed Shop"}
                      </div>
                    </div>

                    <span
                      className={`status-pill ${
                        isShopActive(selectedShop) ? "active" : "blocked"
                      }`}
                    >
                      {isShopActive(selectedShop) ? "ACTIVE" : "BLOCKED"}
                    </span>
                  </div>

                  {/* WALLET BALANCE HIGHLIGHT */}
                  <div className="p-3.5 rounded-xl bg-[#0D111C] border border-[rgba(148,163,184,0.14)] d-flex align-items-center justify-content-between">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-[#64748B] mb-0.5">
                        Available Shop Wallet
                      </div>
                      <div className="fw-extrabold fs-4 text-[#D4AF37]">
                        🪙 {formatCoins(selectedShop.wallet)}
                      </div>
                    </div>
                    <div className="h-10 w-10 rounded-xl bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30 d-flex align-items-center justify-content-center fs-5">
                      <i className="bi bi-wallet2" />
                    </div>
                  </div>

                  {/* OWNER & CONTACT GRID */}
                  <div className="row g-3">
                    <div className="col-6">
                      <div className="p-3 rounded-xl bg-[#0D111C] border border-[rgba(148,163,184,0.14)] h-100">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-[#64748B] mb-1">
                          Owner
                        </div>
                        <div className="fw-bold text-[#F8FAFC] small text-truncate">
                          {selectedShop.owner_full_name || "N/A"}
                        </div>
                        <div className="text-[11px] text-[#D4AF37] font-semibold text-truncate">
                          @{selectedShop.owner_username || "N/A"}
                        </div>
                      </div>
                    </div>

                    <div className="col-6">
                      <div className="p-3 rounded-xl bg-[#0D111C] border border-[rgba(148,163,184,0.14)] h-100">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-[#64748B] mb-1">
                          Mobile Contact
                        </div>
                        <div className="fw-bold text-[#F8FAFC] small">
                          <i className="bi bi-telephone me-1.5 text-[#94A3B8]" />
                          {selectedShop.mobile || "N/A"}
                        </div>
                      </div>
                    </div>

                    {selectedShop.address && (
                      <div className="col-12">
                        <div className="p-3 rounded-xl bg-[#0D111C] border border-[rgba(148,163,184,0.14)]">
                          <div className="text-[10px] font-bold uppercase tracking-wider text-[#64748B] mb-1">
                            Shop Address
                          </div>
                          <div className="fw-medium text-[#CBD5E1] small text-truncate">
                            <i className="bi bi-geo-alt me-1.5 text-[#94A3B8]" />
                            {selectedShop.address}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* ACTIONS */}
                <div className="modal-footer bg-[#0D111C] border-t border-[rgba(148,163,184,0.14)] py-3 px-4 d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-danger text-xs px-3.5 py-2 fw-bold me-auto"
                    onClick={() => {
                      setShopToDelete(selectedShop);
                    }}
                  >
                    <i className="bi bi-trash me-1.5" />
                    Delete Shop
                  </button>

                  <button
                    type="button"
                    className="btn btn-secondary text-xs px-3 py-2"
                    onClick={() => setSelectedShop(null)}
                  >
                    Close
                  </button>

                  <Link
                    to="/transfer-shop"
                    className="btn btn-primary text-xs px-3.5 py-2 fw-bold"
                    onClick={() => setSelectedShop(null)}
                  >
                    <i className="bi bi-send me-1.5" />
                    Transfer Coins
                  </Link>

                  <button
                    type="button"
                    className={`btn text-xs px-3.5 py-2 fw-bold ${
                      isShopActive(selectedShop)
                        ? "btn-outline-danger"
                        : "btn-outline-success"
                    }`}
                    disabled={statusUpdatingId === selectedShop.id}
                    onClick={async () => {
                      const updated = await handleToggleStatus(selectedShop);
                      if (updated) {
                        setSelectedShop(null);
                      }
                    }}
                  >
                    {statusUpdatingId === selectedShop.id ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1.5" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <i
                          className={`bi me-1.5 ${
                            isShopActive(selectedShop)
                              ? "bi-lock-fill"
                              : "bi-unlock-fill"
                          }`}
                        />
                        {isShopActive(selectedShop) ? "Block Shop" : "Unblock Shop"}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {/* =====================================================
          CONFIRM DELETE MODAL
      ===================================================== */}
      {shopToDelete && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1060 }}
          />

          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            style={{ zIndex: 1065 }}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget && !deleting) {
                setShopToDelete(null);
              }
            }}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              style={{ maxWidth: "420px", margin: "10px auto" }}
            >
              <div className="modal-content border border-[rgba(239,68,68,0.3)] bg-[#121827] shadow-2xl rounded-2xl overflow-hidden text-white">
                <div className="modal-header bg-[#0D111C] border-b border-[rgba(148,163,184,0.14)] py-3 px-4 d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2 text-danger">
                    <i className="bi bi-exclamation-triangle-fill fs-5" />
                    <h5 className="modal-title fw-bold fs-6 mb-0 text-white">
                      Confirm Delete Shop
                    </h5>
                  </div>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    aria-label="Close"
                    disabled={deleting}
                    onClick={() => setShopToDelete(null)}
                  />
                </div>

                <div className="modal-body p-4 text-center">
                  <div className="mb-3">
                    <div className="d-inline-flex p-3 rounded-circle bg-danger bg-opacity-10 text-danger mb-2">
                      <i className="bi bi-trash fs-2" />
                    </div>
                  </div>

                  <h6 className="fw-bold mb-2 fs-5">
                    Delete "{shopToDelete.shop_name}"?
                  </h6>

                  <p className="text-secondary small mb-3">
                    Are you sure you want to permanently delete this Shop?
                    All associated data (computers, players, bets, and transaction history) for this shop will be permanently removed.
                  </p>

                  <div className="p-3 rounded-xl bg-[#0D111C] border border-[rgba(148,163,184,0.14)] text-start small">
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-secondary">Shop ID:</span>
                      <span className="fw-bold">#{shopToDelete.id}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-secondary">Owner:</span>
                      <span className="fw-bold">@{shopToDelete.owner_username || "N/A"}</span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="text-secondary">Wallet Coins:</span>
                      <span className="fw-bold text-warning">🪙 {formatCoins(shopToDelete.wallet)}</span>
                    </div>
                  </div>
                </div>

                <div className="modal-footer bg-[#0D111C] border-t border-[rgba(148,163,184,0.14)] py-3 px-4 d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-secondary text-xs px-3.5 py-2"
                    disabled={deleting}
                    onClick={() => setShopToDelete(null)}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="btn btn-danger text-xs px-4 py-2 fw-bold"
                    disabled={deleting}
                    onClick={() => handleDeleteShop(shopToDelete)}
                  >
                    {deleting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1.5" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-trash me-1.5" />
                        Yes, Delete
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Shops;